import * as THREE from "three";

function canvas(size: number): CanvasRenderingContext2D {
  const el = document.createElement("canvas");
  el.width = size;
  el.height = size;
  const ctx = el.getContext("2d");
  if (!ctx) throw new Error("No 2D context");
  return ctx;
}

function tex(ctx: CanvasRenderingContext2D, repeatX: number, repeatY: number): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(ctx.canvas);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeatX, repeatY);
  t.anisotropy = 8;
  t.needsUpdate = true;
  return t;
}

export function grassTexture(repeatX: number, repeatY: number): THREE.CanvasTexture {
  const ctx = canvas(256);
  ctx.fillStyle = "#4a7a32";
  ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 4000; i++) {
    const g = 90 + Math.random() * 70;
    ctx.fillStyle = `rgb(${40 + Math.random() * 30},${g},${30 + Math.random() * 25})`;
    ctx.fillRect(Math.random() * 256, Math.random() * 256, 2, 2);
  }
  return tex(ctx, repeatX, repeatY);
}

export function stuccoTexture(): THREE.CanvasTexture {
  const ctx = canvas(256);
  ctx.fillStyle = "#f3eee6";
  ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 8000; i++) {
    const n = 220 + Math.random() * 30;
    ctx.fillStyle = `rgba(${n},${n - 6},${n - 14},0.35)`;
    ctx.fillRect(Math.random() * 256, Math.random() * 256, 1.5, 1.5);
  }
  return tex(ctx, 2, 1.2);
}

export function brickTexture(): THREE.CanvasTexture {
  const ctx = canvas(256);
  ctx.fillStyle = "#6b3a2a";
  ctx.fillRect(0, 0, 256, 256);
  const bw = 32;
  const bh = 16;
  for (let y = 0, row = 0; y < 256; y += bh, row++) {
    const ox = row % 2 === 0 ? 0 : bw / 2;
    for (let x = -bw; x < 256; x += bw) {
      ctx.fillStyle = `rgb(${140 + Math.random() * 40},${55 + Math.random() * 25},${40 + Math.random() * 15})`;
      ctx.fillRect(x + ox + 1, y + 1, bw - 2, bh - 2);
    }
  }
  return tex(ctx, 3, 2);
}

export function tileTexture(): THREE.CanvasTexture {
  const ctx = canvas(256);
  ctx.fillStyle = "#7a2e1c";
  ctx.fillRect(0, 0, 256, 256);
  for (let y = 0; y < 280; y += 14) {
    const ox = (y / 14) % 2 === 0 ? 0 : 12;
    for (let x = -20; x < 276; x += 24) {
      ctx.fillStyle = `rgb(${170 + Math.random() * 40},${70 + Math.random() * 20},${40 + Math.random() * 15})`;
      ctx.beginPath();
      ctx.ellipse(x + ox, y, 13, 7, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(80,20,10,0.25)";
      ctx.stroke();
    }
  }
  return tex(ctx, 4, 3);
}

export function waterTexture(): THREE.CanvasTexture {
  const ctx = canvas(256);
  const g = ctx.createLinearGradient(0, 0, 256, 256);
  g.addColorStop(0, "#3aa8c9");
  g.addColorStop(0.5, "#1f7fa0");
  g.addColorStop(1, "#2eb0c8");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 256, 256);
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  for (let i = 0; i < 18; i++) {
    ctx.beginPath();
    ctx.arc(40 + i * 12, 80 + Math.sin(i) * 40, 30 + i, 0, Math.PI);
    ctx.stroke();
  }
  const t = tex(ctx, 1, 1);
  t.repeat.set(1, 1);
  return t;
}

export function fieldTexture(width: number, depth: number): THREE.CanvasTexture {
  const ctx = canvas(1024);
  ctx.fillStyle = "#3d8a3a";
  ctx.fillRect(0, 0, 1024, 1024);
  for (let i = 0; i < 6000; i++) {
    ctx.fillStyle = `rgba(${30 + Math.random() * 40},${110 + Math.random() * 50},${30},0.35)`;
    ctx.fillRect(Math.random() * 1024, Math.random() * 1024, 3, 3);
  }
  ctx.strokeStyle = "rgba(255,255,255,0.92)";
  ctx.lineWidth = 6;
  const m = 36;
  ctx.strokeRect(m, m, 1024 - m * 2, 1024 - m * 2);
  ctx.beginPath();
  ctx.moveTo(512, m);
  ctx.lineTo(512, 1024 - m);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(512, 512, 90, 0, Math.PI * 2);
  ctx.stroke();
  const goal = 110;
  const box = 170;
  ctx.strokeRect(m, 512 - goal, box, goal * 2);
  ctx.strokeRect(1024 - m - box, 512 - goal, box, goal * 2);
  const t = tex(ctx, 1, 1);
  t.repeat.set(1, 1);
  void width;
  void depth;
  return t;
}

export function woodTexture(): THREE.CanvasTexture {
  const ctx = canvas(128);
  ctx.fillStyle = "#6b4226";
  ctx.fillRect(0, 0, 128, 128);
  for (let x = 0; x < 128; x += 16) {
    ctx.fillStyle = `rgb(${90 + Math.random() * 40},${55 + Math.random() * 20},${30})`;
    ctx.fillRect(x, 0, 15, 128);
  }
  return tex(ctx, 1, 1);
}
